import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import { MultiSelect } from '@/components/forms/MultiSelect'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { listShareableUsers } from '@/views/Kodi/api/kodiReportsApi'

import { REPORT_VISIBILITY } from './kodiReports.constants'

const personName = (user) =>
  `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username

// One fetch of the org's users per organization, shared by every card's menu
// (same pattern as useOrgUsers). A failed fetch is evicted so the next open retries.
const shareableUsersCache = new Map()

const loadShareableUsers = (dispatch, organizationId) => {
  if (!shareableUsersCache.has(organizationId)) {
    shareableUsersCache.set(
      organizationId,
      listShareableUsers(dispatch, organizationId)
        .then(({ users = [] } = {}) => users)
        .catch(() => {
          shareableUsersCache.delete(organizationId)
          return []
        })
    )
  }
  return shareableUsersCache.get(organizationId)
}

/**
 * Click-to-open popover on a chart's Share button: who can see the chart — only its
 * pinner, them and chosen people, or everyone in the organization. Only the pinner
 * gets it.
 *
 * @import { KodiReportShareMenu } from '@/views/KodiReports/KodiReportShareMenu'
 * @param {object} props
 * @param {object} props.card The chart being shared
 * @param {Function} props.onSave Called with { visibility, viewerIds }; resolves true once saved
 * @param {node} props.children The trigger button
 */
export const KodiReportShareMenu = ({ card, onSave, children }) => {
  const dispatch = useDispatch()
  const { organizationid } = useSelector((state) => state.currentUser)
  const [open, setOpen] = useState(false)
  const [visibility, setVisibility] = useState(REPORT_VISIBILITY.PRIVATE)
  const [viewerIds, setViewerIds] = useState([])
  const [people, setPeople] = useState([])
  const [loadingPeople, setLoadingPeople] = useState(false)
  const [saving, setSaving] = useState(false)

  // Starts from the chart's current sharing each time it opens, so a cancelled edit
  // doesn't linger.
  useEffect(() => {
    if (!open) {
      return
    }
    const { shared_with: sharedWith = [] } = card
    setVisibility(card.visibility || REPORT_VISIBILITY.ORG)
    setViewerIds(sharedWith.map((user) => user.user_id))
    setLoadingPeople(true)
    loadShareableUsers(dispatch, organizationid)
      .then(setPeople)
      .finally(() => setLoadingPeople(false))
  }, [open])

  const pinnerId = card.pinned_by?.user_id
  const isShared = visibility === REPORT_VISIBILITY.SHARED
  // The pinner always sees their chart, so they aren't offered.
  const options = people
    .filter((user) => user.id !== pinnerId)
    .map((user) => ({ value: user.id, label: personName(user) }))
  // Only people still offered: a viewer since deactivated isn't, and saving them
  // would be refused.
  const selectedIds = viewerIds.filter((id) => options.some((option) => option.value === id))
  const choices = [
    { accessor: REPORT_VISIBILITY.PRIVATE, label: 'Only me', tooltip: 'Only you can see it' },
    { accessor: REPORT_VISIBILITY.SHARED, label: 'Specific people' },
    {
      accessor: REPORT_VISIBILITY.ORG,
      label: 'Everyone',
      tooltip: 'Everyone in your organization',
    },
  ]

  const handleSave = async () => {
    setSaving(true)
    // Specific people with nobody left is just Only me.
    const shareWithPeople = isShared && !isEmpty(selectedIds)
    const saved = await onSave(
      shareWithPeople
        ? { visibility, viewerIds: selectedIds }
        : { visibility: isShared ? REPORT_VISIBILITY.PRIVATE : visibility }
    )
    setSaving(false)
    if (saved) {
      setOpen(false)
    }
  }

  const content = (
    <div className="field-stack" data-testid={`kodi-report-share-menu-${card.card_id}`}>
      <strong>Who can see this chart</strong>
      <SegmentedControl
        items={choices.map((choice) => ({
          ...choice,
          active: visibility === choice.accessor,
          onClick: () => setVisibility(choice.accessor),
        }))}
      />
      {isShared && (
        <MultiSelect
          options={options}
          value={options.filter((option) => selectedIds.includes(option.value))}
          placeholder="Search people…"
          loading={loadingPeople}
          condenseOptions={false}
          showFullOptions
          // Inline, not portaled to <body>: a click in a portaled menu reads as a
          // click outside the popover and would close it.
          menuPortalTarget={null}
          onChange={(selected) => setViewerIds((selected ?? []).map((option) => option.value))}
        />
      )}
      <div className="flex-align-center small-gap">
        <Button small onClick={() => setOpen(false)} disabled={saving}>
          Cancel
        </Button>
        <Button
          small
          brand
          onClick={handleSave}
          // Until the people list loads (or if it fails), nobody looks selected,
          // and saving would quietly make the chart private.
          disabled={saving || (isShared && (loadingPeople || isEmpty(options)))}
          loading={saving}
          data-testid="kodi-report-share-save"
        >
          Save
        </Button>
      </div>
    </div>
  )

  return (
    <InteractiveTooltip
      clickable
      theme="light"
      size="large"
      placement="bottom-end"
      open={open}
      setOpen={(next) => !saving && setOpen(next)}
      content={content}
    >
      {children}
    </InteractiveTooltip>
  )
}
